import { startTransition, type TransitionStartFunction } from 'react';
import { unstable_rethrow } from 'next/navigation';
import { RSCSerializationError } from './errors/RSCSerializationError';

/**
 * Recursively asserts that parameters are RSC-serializable.
 * Throws a clear error if any non-serializable values are found.
 *
 * RSC-serializable values include:
 * - Primitives: string, number, bigint, boolean, undefined, null
 * - Symbols registered in the global Symbol registry via Symbol.for()
 * - Iterables: String, Array, Map, Set, TypedArray, ArrayBuffer
 * - Date objects
 * - FormData instances
 * - Plain objects with serializable properties
 * - Functions that are Server Functions
 * - Promises.
 *
 * Non-serializable values include:
 * - React elements or JSX
 * - Functions (except Server Functions)
 * - Class instances (except built-ins like Date, Map, Set, etc.)
 * - Objects with null prototype
 * - Symbols not registered globally
 * - Events from event handlers.
 *
 * @param params - The parameters to validate.
 * @param path - Internal parameter for tracking the current path in nested objects.
 * @returns The original parameters if they are valid.
 * @throws {RSCSerializationError} If any non-serializable values are found.
 */
export function assertRSCSerializableParams<T>(
  params: T,
  path: Array<string> = []
): T {
  // Handle null and undefined
  if (params === null || params === undefined) {
    return params;
  }

  // Check for functions
  // TODO: Add a way to allow other server functions.
  if (typeof params === 'function') {
    throw new RSCSerializationError(
      `Functions are not RSC-serializable${formatPath(path)}.`
    );
  }

  // Check for symbols
  // TODO: Add a way to allow Symbols registered in the global Symbol registry.
  if (typeof params === 'symbol') {
    throw new RSCSerializationError(
      `Symbols are not RSC-serializable${formatPath(path)}.`
    );
  }

  // Primitives are serializable
  if (
    typeof params === 'string' ||
    typeof params === 'number' ||
    typeof params === 'bigint' ||
    typeof params === 'boolean'
  ) {
    return params;
  }

  // Built-in serializable objects
  if (
    params instanceof Date ||
    params instanceof FormData ||
    params instanceof Map ||
    params instanceof Set ||
    params instanceof ArrayBuffer ||
    ArrayBuffer.isView(params) || // TypedArrays
    params instanceof Promise
  ) {
    // For Map and Set, we need to check their contents
    if (params instanceof Map) {
      for (const [key, value] of params) {
        assertRSCSerializableParams(key, [...path, `Map.key`]);
        assertRSCSerializableParams(value, [...path, `Map.value`]);
      }
    } else if (params instanceof Set) {
      let index = 0;
      for (const value of params) {
        assertRSCSerializableParams(value, [...path, `Set[${index}]`]);
        index++;
      }
    }
    return params;
  }

  // Arrays: recursively check each element
  if (Array.isArray(params)) {
    params.forEach((item, index) => {
      assertRSCSerializableParams(item, [...path, `[${index}]`]);
    });
    return params;
  }

  // Objects: check if it's a plain object and recursively validate properties
  if (typeof params === 'object') {
    // Check for objects with null prototype
    const proto = Object.getPrototypeOf(params);
    if (proto === null) {
      throw new RSCSerializationError(
        `Objects with null prototype are not RSC-serializable${formatPath(path)}.`
      );
    }

    // Check if it's a plain object (not a class instance)
    if (proto !== Object.prototype) {
      throw new RSCSerializationError(
        `Class instances are not RSC-serializable. Found instance of: ${params.constructor?.name || 'unknown class'}${formatPath(path)}.`
      );
    }

    // Check for circular references by attempting JSON serialization
    try {
      JSON.stringify(params);
    } catch (error) {
      if (error instanceof TypeError && error.message.includes('circular')) {
        throw new RSCSerializationError(
          `Objects with circular references are not RSC-serializable${formatPath(path)}.`
        );
      }

      // Re-throw other JSON errors
      throw error;
    }

    // Recursively validate all properties
    for (const [key, value] of Object.entries(params)) {
      assertRSCSerializableParams(value, [...path, key]);
    }

    return params;
  }

  // If we reach here, it's an unknown type
  throw new RSCSerializationError(
    `Unknown type "${typeof params}" is not RSC-serializable${formatPath(path)}.`
  );
}

/**
 * Formats a path array into a readable string for error messages.
 * @param path - The path array to format.
 * @returns A formatted path string, or empty string if path is empty.
 */
function formatPath(path: Array<string>): string {
  return path.length > 0 ? ` at path: ${path.join('.')}` : '';
}

/**
 * Adapts a server function to be used as a form submitter.
 * @param action - The server function to adapt.
 * @param startTransitionFn - The function to use to start the transition.
 * Defaults to {@link startTransition}.
 * @returns A function that can be used as a form submitter.
 */
export function actionToFormSubmitterAdapter<T extends NonNullable<unknown>>(
  action: () => Promise<{ error: T }>,
  startTransitionFn: TransitionStartFunction = startTransition
): () => Promise<void> {
  return async () => {
    let promise: Promise<{ error: T }> | undefined;
    startTransitionFn(async () => {
      promise = action();
      await promise;
    });

    let result: { error: T } | undefined;
    try {
      // if an error is returned, assign it to the result variable
      result = await promise!;
    } catch (error) {
      let isNextJsError = false;
      try {
        // if an error is thrown but it is a Next.js-specific error, then it
        // will be handled by the transition and we will set the flag to true
        unstable_rethrow(error);
      } catch {
        isNextJsError = true;
      }

      // if it is not a Next.js-specific error, then we need to re-throw it
      if (!isNextJsError) {
        throw error;
      }
    }

    // if the result represents an error, throw it
    if (result) {
      throw new Error('The server function returned an error.', {
        cause: result.error
      });
    }
  };
}
